import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import axios from "axios";
import {
  ArrayChip,
  AutoCompleteWithSearch,
  CurrencyDisplay,
  CustomTable,
  EllipsisLabel,
  ModalError,
  ModalSuccess,
  ModalWarning,
  PrimaryButton,
  PrimaryCheckButton,
  PrimaryCloseButton,
  PrimaryDeleteButton,
  PrimaryEditButton,
  SearchTextInput,
} from "components";
import { HeaderTitle } from "layouts";
import { debounce } from "lodash";
import { Fragment, useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router";
import { hardBaseUrl } from "services/urlConstant";
import { getErrors, stringToArray } from "utils";

const ModalPreviewPotentialClaim = ({
  open,
  onClose,
  loadingPage,
  setLoadingPage,
  pageDetail,
  queryParamsPreview,
  setQueryParamsPreview,
  tableTotalPage,
  queryParams,
  selectedSong,
  setSelectedSong,
}) => {
  const history = useHistory();
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const [payload, setPayload] = useState(null);
  const [optionCatalogue, setOptionCatalogue] = useState([]);
  const [loadingFilter, setLoadingFilter] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [filterParams, setFilterParams] = useState({
    page: 1,
    size: 10,
    search: "",
  });

  const handleChangeQueryParams = (value, key) => {
    setQueryParamsPreview(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };
  const handleChangeFilterParams = (value, key) => {
    setFilterParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };
  const handleChangeSearch = event => {
    const { value } = event?.target;
    handleChangeQueryParams(value, "search");
  };
  const handleCorrection = value => {
    setPayload(prev => ({
      ...prev,
      record_id: prev?.id,
      song_id: value?.id,
    }));
  };
  const handleSaveCorrection = () => {
    setQueryParamsPreview(currentState => {
      const isrcList = stringToArray(payload?.isrc, ",");

      const updatedCorrections = [...(currentState.corrections ?? [])];

      isrcList.forEach(isrc => {
        const existingIndex = updatedCorrections.findIndex(
          correction => correction.isrc === isrc
        );

        const newCorrection = {
          song_id: payload?.song_id,
          isrc,
        };

        if (existingIndex >= 0) {
          updatedCorrections[existingIndex] = newCorrection;
        } else {
          updatedCorrections.push(newCorrection);
        }
      });

      return {
        ...currentState,
        corrections: updatedCorrections,
      };
    });
    setPayload(null);
  };
  const handleDelete = option => {
    ModalWarning(
      `Are you sure you want to delete ${option?.song_title} ?`,
      "Delete Data"
    ).then(res => res?.isConfirmed && handleSubmitDelete(option));
  };
  const handleSubmitDelete = option => {
    const parentSongId = option?.matched_song_id;

    setSelectedSong(prev => {
      const index = prev.findIndex(item => item.song_id === parentSongId);

      if (index === -1) return prev;

      const updated = [...prev];
      const parent = { ...updated[index] };

      const isSelectAllMode = parent.selected_record_ids.length === 0;

      if (isSelectAllMode) {
        if (!parent.not_selected_record_ids.includes(option.id)) {
          parent.not_selected_record_ids = [
            ...parent.not_selected_record_ids,
            option.id,
          ];
        }
      } else {
        parent.selected_record_ids = parent.selected_record_ids.filter(
          id => id !== option.id
        );
      }

      const isEmptySelected = parent.selected_record_ids.length === 0;
      const isEmptyNotSelected = parent.not_selected_record_ids.length === 0;

      if (isEmptySelected && isEmptyNotSelected) {
        return prev.filter(item => item.song_id !== parentSongId);
      }

      updated[index] = parent;
      return updated;
    });
  };

  const getOptionCatalogue = async () => {
    try {
      setLoadingFilter(true);
      const res = await axios.get(
        `${hardBaseUrl}/potential-claim/preview/songs`,
        {
          headers,
          params: filterParams,
        }
      );
      const { data, meta } = res?.data;
      setOptionCatalogue(prev => {
        const combined = filterParams?.page === 1 ? data : [...prev, ...data];
        setHasMore(meta?.total > combined?.length);
        return combined;
      });
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingFilter(false);
    }
  };
  const debounceOptionCatalogue = useCallback(
    debounce(() => {
      getOptionCatalogue();
    }, 500),
    [filterParams]
  );
  const handleClaim = async () => {
    const { dsp_id, header_ids, matching_key } = queryParams;
    const formattedSongs = selectedSong.map(item => {
      const hasSelected = item.selected_record_ids?.length > 0;
      const hasNotSelected = item.not_selected_record_ids?.length > 0;

      if (!hasSelected && !hasNotSelected) {
        return { song_id: item.song_id };
      }
      return {
        song_id: item.song_id,
        ...(hasSelected && {
          selected_record_ids: item.selected_record_ids,
        }),
        ...(hasNotSelected && {
          not_selected_record_ids: item.not_selected_record_ids,
        }),
      };
    });
    const modifiedPayload = {
      dsp_id: dsp_id,
      header_ids: header_ids?.split(",").map(Number) || [],
      matching_key: matching_key === 0 ? "" : matching_key,
      songs: formattedSongs,
      ...queryParamsPreview,
    };

    try {
      setLoadingPage(true);
      await axios.post(
        `${hardBaseUrl}/potential-claim/claim`,
        modifiedPayload,
        { headers }
      );
      ModalSuccess(
        "You can check the progress of the claim process in Potential Claim Log",
        "Data is being processed"
      ).then(() => history.push("/admin/publisher/potential-claim-log"));
    } catch (error) {
      ModalError(getErrors(error));
    } finally {
      setLoadingPage(false);
    }
  };

  useEffect(() => {
    if (payload?.id) {
      const isSearching = Boolean(filterParams?.search);
      if (isSearching) {
        debounceOptionCatalogue();
        return () => {
          debounceOptionCatalogue.cancel();
        };
      } else {
        getOptionCatalogue();
      }
    }
  }, [filterParams, debounceOptionCatalogue, payload]);
  useEffect(() => {
    if (!payload?.id) {
      setFilterParams({
        page: 1,
        size: 10,
        search: "",
      });
    }
  }, [payload]);
  useEffect(() => {
    setPayload(prev => (prev ? null : prev));
  }, [
    queryParamsPreview?.page,
    queryParamsPreview?.per_page,
    queryParamsPreview?.search,
  ]);

  return (
    <Dialog fullScreen open={open} onClose={onClose}>
      <DialogTitle>
        <HeaderTitle
          title="Preview Potential Claim"
          backButton
          onReturn={onClose}
        />
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
          flexWrap={{ xs: "wrap", lg: "nowrap" }}
          mb={2}
        >
          <Box
            display="flex"
            gap={2}
            flex={1}
            minWidth={0}
            flexWrap={{ xs: "wrap", md: "nowrap" }}
          >
            <Box sx={{ width: { xs: "100%", sm: 220 } }}>
              <SearchTextInput
                placeholder="Search"
                value={queryParamsPreview?.search}
                onChange={handleChangeSearch}
                fullWidth
              />
            </Box>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                border: "1px solid #D1D5DC",
                borderRadius: "6px",
                p: 1,
              }}
            >
              <Box
                display="flex"
                justifyContent="space-around"
                alignItems="center"
                flexWrap="wrap"
                rowGap={1}
              >
                {pageDetailList(pageDetail).map(item => (
                  <Box
                    key={item.title}
                    display="flex"
                    gap={1}
                    alignItems="center"
                  >
                    <Typography color="text.secondary">{item.title}</Typography>
                    <Typography fontWeight={700}>{item.value}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
          <Box
            width={{ xs: "100%", lg: "180px" }}
            display="flex"
            justifyContent={{ xs: "stretch", lg: "flex-end" }}
          >
            <PrimaryButton label="Confirm Claim" onClick={handleClaim} />
          </Box>
        </Box>
        <CustomTable
          isLoading={loadingPage}
          columnTable={columnTable({
            payload,
            setPayload,
            loadingFilter,
            optionCatalogue,
            filterParams,
            handleChangeFilterParams,
            setFilterParams,
            hasMore,
            handleCorrection,
            handleSaveCorrection,
            handleDelete,
            fontSize: "12px",
          })}
          data={pageDetail?.items || []}
          page={queryParamsPreview?.page}
          pageSize={queryParamsPreview?.per_page}
          pageCount={tableTotalPage}
          handleChangePage={(_, page) => handleChangeQueryParams(page, "page")}
          handleChangePageSize={e =>
            handleChangeQueryParams(e?.target?.value, "per_page")
          }
          sortValue={queryParamsPreview?.sort}
          onRequestSort={sortBy => {
            return handleChangeQueryParams(
              `${sortBy} ${
                queryParamsPreview.sort === `${sortBy} asc` ? "desc" : "asc"
              }`,
              "sort"
            );
          }}
          pageSizeList={[
            { name: 100 },
            { name: 150 },
            { name: 200 },
            { name: 250 },
          ]}
        />
      </DialogContent>
    </Dialog>
  );
};

const pageDetailList = pageDetail => [
  {
    title: "DSP",
    value: pageDetail?.dsp_name,
  },
  {
    title: "Total Traffic",
    value: <CurrencyDisplay value={pageDetail?.total_traffic} />,
  },
  {
    title: "Total Revenue",
    value: <CurrencyDisplay prefix="Rp " value={pageDetail?.total_revenue} />,
  },
];
const columnTable = ({
  payload,
  setPayload,
  loadingFilter,
  optionCatalogue,
  filterParams,
  handleChangeFilterParams,
  setFilterParams,
  hasMore,
  handleCorrection,
  handleSaveCorrection,
  handleDelete,
  fontSize,
}) => [
  {
    name: "song_title",
    title: "Song Title",
    useSort: true,
    sort_key: "song_title",
    dense: true,
    renderText: item => (
      <EllipsisLabel fontSize={fontSize} fontWeight={700} text={item} />
    ),
  },
  {
    name: "composer",
    title: "Composer/Author",
    useSort: true,
    sort_key: "composer",
    dense: true,
    renderText: item => {
      const list = stringToArray(item, "|");
      return <ArrayChip max={1} list={list} fontSize={fontSize} />;
    },
  },
  {
    name: "isrc",
    title: "ISRC",
    useSort: true,
    sort_key: "isrc",
    dense: true,
    renderText: item => {
      const list = stringToArray(item, ",");
      return <ArrayChip max={1} list={list} fontSize={fontSize} />;
    },
  },
  {
    name: "period_date",
    title: "Period Date",
    dense: true,
    renderText: item => (
      <Typography fontSize={fontSize}>{item || "-"}</Typography>
    ),
  },
  {
    name: "traffic",
    title: "Traffic",
    headerAlign: "right",
    useSort: true,
    sort_key: "traffic",
    dense: true,
    renderText: item => <CurrencyDisplay value={item} fontSize={fontSize} />,
  },
  {
    name: "revenue",
    title: "Revenue",
    headerAlign: "right",
    useSort: true,
    sort_key: "revenue",
    dense: true,
    renderText: item => (
      <CurrencyDisplay value={item} prefix="Rp" fontSize={fontSize} />
    ),
  },
  {
    name: "all",
    title: "Catalogue Song",
    highlight: true,
    useSort: true,
    sort_key: "matched_song_title",
    dense: true,
    renderText: item => {
      const list = stringToArray(item?.matched_song_composer, ",");
      return payload?.id === item?.id ? (
        <AutoCompleteWithSearch
          placeholder="Song Catalogue"
          options={optionCatalogue}
          value={
            optionCatalogue.find(option => option.id === payload?.song_id) ??
            null
          }
          onChange={value => handleCorrection(value)}
          inputValue={filterParams?.search}
          uniqueKey="id"
          optionLabel="song_title"
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          onInputChange={value => handleChangeFilterParams(value, "search")}
          renderOption={(props, option) => {
            const composers = stringToArray(option?.composer, ",");
            return (
              <Box
                component="li"
                {...props}
                key={option?.id || option?.song_title}
              >
                <Grid container direction="column">
                  <Grid item>{option?.song_title || "-"}</Grid>
                  <Grid item>
                    <ArrayChip max={2} list={composers} fontSize={fontSize} />
                  </Grid>
                </Grid>
              </Box>
            );
          }}
          setQueryParams={setFilterParams}
          hasMore={hasMore}
          loading={loadingFilter}
          width="100%"
        />
      ) : (
        <Box>
          <Typography fontSize={fontSize}>
            {item?.matched_song_title || "-"}
          </Typography>
          <ArrayChip max={1} list={list} fontSize={fontSize} />
        </Box>
      );
    },
  },
  {
    name: "all",
    title: "Action",
    dense: true,
    renderText: item => {
      return (
        <Grid container columnGap={1} width={68} alignItems="center">
          {payload?.id === item?.id ? (
            <Fragment>
              <Grid item>
                <PrimaryCheckButton
                  tooltipTitle="Save"
                  onClick={handleSaveCorrection}
                />
              </Grid>
              <Grid item>
                <PrimaryCloseButton
                  tooltipTitle="Cancel"
                  onClick={() => setPayload(null)}
                />
              </Grid>
            </Fragment>
          ) : (
            <Fragment>
              <Grid item>
                <PrimaryEditButton
                  tooltipTitle="Edit"
                  onClick={() => setPayload(item)}
                  disabled={payload ? payload?.id !== item?.id : false}
                />
              </Grid>
              <Grid item>
                <PrimaryDeleteButton
                  tooltipTitle="Delete"
                  onClick={() => handleDelete(item)}
                  disabled={payload ? payload?.id !== item?.id : false}
                />
              </Grid>
            </Fragment>
          )}
        </Grid>
      );
    },
  },
];
export default ModalPreviewPotentialClaim;
